import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Smartphone, Monitor, LogOut, ShieldCheck } from "lucide-react";
import { API, getAuthHeaders } from "@/App";

function deviceIcon(label) {
  const l = (label || "").toLowerCase();
  if (l.includes("iphone") || l.includes("android") || l.includes("ipad")) return Smartphone;
  return Monitor;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function DevicesDialog({ open, onOpenChange }) {
  const [loading, setLoading] = useState(false);
  const [devices, setDevices] = useState([]);
  const [revoking, setRevoking] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API}/me/devices`, { headers: getAuthHeaders(), credentials: "include" });
      if (!res.ok) throw new Error("No se pudieron cargar los dispositivos");
      const data = await res.json();
      setDevices(data.devices || []);
    } catch (e) { setError(e.message || "Error"); }
    setLoading(false);
  };

  useEffect(() => { if (open) load(); }, [open]);

  const revoke = async (deviceId) => {
    setRevoking(deviceId);
    try {
      const res = await fetch(`${API}/me/devices/${encodeURIComponent(deviceId)}`, {
        method: "DELETE", headers: getAuthHeaders(), credentials: "include",
      });
      if (!res.ok) throw new Error("No se pudo cerrar la sesión");
      setDevices((prev) => prev.filter((d) => d.device_id !== deviceId));
    } catch (e) { setError(e.message || "Error"); }
    setRevoking(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#17212b] text-white border-[#0e1621] max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-blue-400" /> Mis dispositivos</DialogTitle>
          <DialogDescription className="text-gray-400">
            Administra las sesiones activas en otros dispositivos. Puedes cerrar cualquiera de forma remota.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
        ) : error ? (
          <div className="text-red-400 text-sm py-4">{error}</div>
        ) : devices.length === 0 ? (
          <div className="text-gray-400 text-sm py-4">No hay sesiones activas.</div>
        ) : (
          <div className="space-y-2">
            {devices.map((d) => {
              const Icon = deviceIcon(d.device_label);
              return (
                <div key={d.device_id} className={`flex items-center justify-between gap-3 p-3 rounded-lg border ${d.is_current ? "border-blue-500/60 bg-blue-500/10" : "border-[#2b3a4a] bg-[#0e1621]"}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <Icon className="w-5 h-5 text-gray-300 shrink-0" />
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{d.device_label}{d.is_current && <span className="ml-2 text-xs text-blue-300">(este dispositivo)</span>}</div>
                      <div className="text-xs text-gray-400 truncate">Activo: {fmtDate(d.last_used_at || d.created_at)}</div>
                    </div>
                  </div>
                  {!d.is_current && (
                    <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300 hover:bg-red-500/10" disabled={revoking === d.device_id} onClick={() => revoke(d.device_id)}>
                      {revoking === d.device_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><LogOut className="w-4 h-4 mr-1" />Cerrar</>}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
